import React, { useEffect, useState } from "react";

import {
	FrontendBatchType,
	FrontendCompanyResultFieldHierarchyNodeMappingPackage,
	FrontendCompanyTestCategory,
	FrontendCompanyTestHierarchyNode,
	FrontendCompanyTestResultField,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleInstance,
	FrontendUserInfo,
} from "node-library/dist/portal";
import { useTranslation } from "react-i18next";

import { Card } from "@mui/material";
import { FrontendBatchPageTestBatchDetails } from "node-library/dist/structures/frontendSampleTestBatchPageDefinitions";
import {
	FrontendCompanyWorkflow,
	FrontendCompanyWorkflowSteps,
} from "node-library/dist/structures/frontendTestWorkflowStructureDefinitions";

import { GreenButton } from "../../../../common-components/button";
import AddIcon from "@mui/icons-material/Add";
import ResultCell from "../components/resultCell";
import { CustomSearchBar } from "../../../../common-components/inputs";
import AddOrEditResultFieldModal from "../components/addOrEditResultFieldModal";
import { Severity } from "node-library/dist/constants";
import { RedErrorIcon } from "../../../../common-components/toast/errorIcons";
import { sendPOSTRequest } from "../../../../requestHelper/sendRequest";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../../common-components/toast/successIcons";
import { overwriteFrontendCompanyInfo } from "../../../../helpers/companyHelper";

interface Props {
	user: FrontendUserInfo;
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
}

export default function ResultStructureTab(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { user, company, setCompany } = props;

	// const hasCategoryToDisplay: boolean =
	// 	company.companyTestCategories.length > 0;
	// const [selectedTestCategory, setSelectedTestCategory] = useState<
	// 	FrontendCompanyTestCategory | undefined
	// >(hasCategoryToDisplay ? company.companyTestCategories[0] : undefined);

	const [addOrEditResultFieldModalOpen, setAddOrEditResultFieldModalOpen] =
		useState<boolean>(false);

	const [searchText, setSearchText] = useState<string>("");

	const [filteredResults, setFilteredResults] = useState<
		Array<FrontendCompanyTestResultField>
	>(company.testResultAndExpectationFields || []);

	const [selectedResultField, setSelectedResultField] = useState<
		FrontendCompanyTestResultField | undefined
	>();

	const [loading, setLoading] = useState<boolean>(false);

	function filterResults() {
		// 1. If there's no search text, just show everything from the category
		if (!searchText.trim()) {
			setFilteredResults(company.testResultAndExpectationFields || []);
			return;
		}

		const lowerSearchText = searchText.toLowerCase();

		// 2. Perform the filter
		const filtered = company.testResultAndExpectationFields.filter((field) => {
			// Check if the Field Name matches
			const nameMatches = field.fieldName
				.toLowerCase()
				.includes(lowerSearchText);

			// Check if any of the associated Package Names match the search text
			const packageMatches = field.nodeMappingPackageIds.some((packageId) => {
				// Find the actual package object in the company's master list
				const pkg = company.hierarchyNodeMappingPackages.find(
					(p) => p.packageId === packageId,
				);
				return pkg?.packageName.toLowerCase().includes(lowerSearchText);
			});

			return nameMatches || packageMatches;
		});

		setFilteredResults(filtered || []);
	}

	useEffect(() => {
		filterResults();
	}, [company]);
	const resultFilterBar: React.ReactNode = (
		<CustomSearchBar
			id="filter-result-searchbar"
			searchText={searchText}
			setSearchText={setSearchText}
			placeholder={translation("Search")}
			searchButtonText={translation("Search")}
			onSearch={() => {
				filterResults();
			}}
		></CustomSearchBar>
	);

	const createResultFieldButton: React.ReactNode = (
		<GreenButton
			key="create-result-field-button"
			id="create-result-field-button"
			size="flex"
			children={
				<>
					<AddIcon></AddIcon>
					{translation("Result")}
				</>
			}
			onClick={() => {
				setSelectedResultField(undefined);
				setAddOrEditResultFieldModalOpen(true);
			}}
		></GreenButton>
	);

	// Inside ResultStructureTab.tsx
	const resultFieldCells: Array<React.ReactNode> = filteredResults
		? filteredResults.map((field) => {
				// Resolve IDs to Names here
				const resolvedNames = field.nodeMappingPackageIds.map((id) => {
					const pkg = company.hierarchyNodeMappingPackages.find(
						(p: FrontendCompanyResultFieldHierarchyNodeMappingPackage) =>
							p.packageId === id,
					);
					return pkg ? pkg.packageName : id; // Fallback to ID if name not found
				});

				return (
					<ResultCell
						key={field.fieldId} // Always use a unique key
						result={field}
						packageNames={resolvedNames} // Pass the names here
						goToViewResultDetails={() => {
							setSelectedResultField(field);
							setAddOrEditResultFieldModalOpen(true);
						}}
					/>
				);
			})
		: [];

	async function addOrEditResult(
		result: FrontendCompanyTestResultField,
		isEdit: boolean,
	) {
		try {
			let response;
			setLoading(true);
			if (isEdit) {
				response = await sendPOSTRequest(
					"/structures/update-test-result-field",
					{
						updatedField: result,
					},
				);

				overwriteFrontendCompanyInfo(
					{
						...company,
						testResultAndExpectationFields:
							company.testResultAndExpectationFields.map((field) => {
								return field.fieldId === result.fieldId ? result : field;
							}),
					},
					setCompany,
				);
			} else {
				response = await sendPOSTRequest(
					"/structures/create-test-result-field",
					{
						newResultField: result,
					},
				);
				const resultFieldId: string = response.newFieldId;

				const finalResult: FrontendCompanyTestResultField = {
					...result,
					fieldId: resultFieldId,
				};

				overwriteFrontendCompanyInfo(
					{
						...company,
						testResultAndExpectationFields: [
							...company.testResultAndExpectationFields,
							finalResult,
						],
					},
					setCompany,
				);
			}

			openSnackbar(
				translation("ResultFieldUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />,
			);

			setAddOrEditResultFieldModalOpen(false);
		} catch (error: any) {
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}
	return (
		<>
			{company.testResultAndExpectationFields.length > 0 ? (
				<>
					<AddOrEditResultFieldModal
						company={company}
						addOrEditResult={addOrEditResult}
						open={addOrEditResultFieldModalOpen}
						setOpen={setAddOrEditResultFieldModalOpen}
						resultField={selectedResultField}
						loading={loading}
					></AddOrEditResultFieldModal>
					<div className={`batch-details-workflow-container`}>
						<div>{createResultFieldButton}</div>
						<div>{resultFilterBar}</div>
						<div className="view-order-grid-table">{resultFieldCells}</div>
					</div>
				</>
			) : (
				<div>{translation("NoTestCategoryToDisplay")}</div>
			)}
		</>
	);
}
